import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { DesignMode, getDesignTokens } from './designTokens';

type SiteButtonTone = 'primary' | 'secondary' | 'quiet' | 'success' | 'danger';

export type SiteButtonProps = Omit<ButtonProps, 'color' | 'variant'> & {
  tone?: SiteButtonTone;
  mode?: DesignMode;
};

const getToneStyles = (
  mode: DesignMode,
  tokens: ReturnType<typeof getDesignTokens>
) =>
  ({
    primary: {
      color: tokens.colors.text,
      background:
        mode === 'light'
          ? 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(245,245,247,0.88))'
          : 'linear-gradient(180deg, rgba(255,255,255,0.15), rgba(255,255,255,0.055))',
      borderColor:
        mode === 'light' ? 'rgba(29,29,31,0.14)' : 'rgba(255,255,255,0.22)',
      boxShadow:
        mode === 'light'
          ? 'inset 0 1px 0 rgba(255,255,255,0.98), 0 10px 24px rgba(0,0,0,0.1)'
          : 'inset 0 1px 0 rgba(255,255,255,0.2), 0 14px 34px rgba(0,0,0,0.26)',
      '&:hover': {
        background:
          mode === 'light'
            ? 'linear-gradient(180deg, #ffffff, #f5f5f7)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.2), rgba(255,255,255,0.075))',
        transform: 'translateY(-1px) scale(1.018)',
        boxShadow:
          mode === 'light'
            ? 'inset 0 1px 0 rgba(255,255,255,1), 0 14px 30px rgba(0,0,0,0.12)'
            : 'inset 0 1px 0 rgba(255,255,255,0.24), 0 18px 40px rgba(0,0,0,0.3)'
      }
    },
    secondary: {
      color: tokens.colors.text,
      background:
        mode === 'light'
          ? 'linear-gradient(180deg, rgba(255,255,255,0.74), rgba(255,255,255,0.42))'
          : 'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.055))',
      borderColor:
        mode === 'light' ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.34)',
      boxShadow:
        mode === 'light'
          ? 'inset 0 1px 0 rgba(255,255,255,0.96), 0 9px 22px rgba(0,0,0,0.08)'
          : 'inset 0 1.5px 0 rgba(255,255,255,0.36), inset 0 -1.5px 0 rgba(255,255,255,0.14), inset -14px -14px 26px rgba(255,255,255,0.12), 0 12px 28px rgba(0,0,0,0.22)',
      '&:hover': {
        background:
          mode === 'light'
            ? 'linear-gradient(180deg, rgba(255,255,255,0.82), rgba(255,255,255,0.52))'
            : 'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))',
        transform: 'translateY(-1px) scale(1.018)'
      }
    },
    quiet: {
      color: tokens.colors.text,
      background:
        mode === 'light'
          ? 'linear-gradient(180deg, rgba(255,255,255,0.66), rgba(255,255,255,0.36))'
          : 'linear-gradient(180deg, rgba(255,255,255,0.1), rgba(255,255,255,0.035))',
      borderColor:
        mode === 'light' ? 'rgba(15,23,42,0.1)' : 'rgba(255,255,255,0.2)',
      boxShadow:
        mode === 'light'
          ? 'inset 0 1px 0 rgba(255,255,255,0.92), 0 8px 18px rgba(0,0,0,0.055)'
          : 'inset 0 1.5px 0 rgba(255,255,255,0.18), inset 0 -1.5px 0 rgba(255,255,255,0.08), 0 10px 22px rgba(0,0,0,0.16)',
      '&:hover': {
        background:
          mode === 'light'
            ? 'linear-gradient(180deg, rgba(255,255,255,0.78), rgba(255,255,255,0.44))'
            : 'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0.055))',
        transform: 'translateY(-1px) scale(1.018)'
      }
    },
    success: {
      color: '#f5fff9',
      background:
        mode === 'light'
          ? 'linear-gradient(180deg, rgba(112,224,169,0.7), rgba(112,224,169,0.7))'
          : 'linear-gradient(180deg, rgba(75,190,134,0.66), rgba(75,190,134,0.66))',
      borderColor:
        mode === 'light' ? 'rgba(32,178,107,0.76)' : 'rgba(103,232,165,0.7)',
      boxShadow:
        mode === 'light'
          ? 'inset 0 1.5px 0 rgba(255,255,255,0.66), inset 0 -1.5px 0 rgba(5,46,28,0.14), inset 0 0 18px rgba(255,255,255,0.16), 0 12px 30px rgba(32,178,107,0.24), 0 0 0 1px rgba(255,255,255,0.38)'
          : 'inset 0 1.5px 0 rgba(255,255,255,0.32), inset 0 -1.5px 0 rgba(103,232,165,0.12), inset 0 0 18px rgba(255,255,255,0.08), 0 14px 32px rgba(32,178,107,0.22), 0 0 22px rgba(103,232,165,0.1)',
      '&::before': {
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0) 62%)'
      },
      '&::after': {
        display: 'none'
      },
      '&:hover': {
        background:
          mode === 'light'
            ? 'linear-gradient(180deg, rgba(121,234,178,0.76), rgba(121,234,178,0.76))'
            : 'linear-gradient(180deg, rgba(84,205,146,0.72), rgba(84,205,146,0.72))',
        transform: 'translateY(-1px) scale(1.035)'
      }
    },
    danger: {
      color: '#fff7f7',
      background:
        mode === 'light'
          ? 'linear-gradient(180deg, rgba(225,105,121,0.7), rgba(225,105,121,0.7))'
          : 'linear-gradient(180deg, rgba(190,82,103,0.66), rgba(190,82,103,0.66))',
      borderColor:
        mode === 'light' ? 'rgba(255,107,107,0.78)' : 'rgba(255,107,107,0.68)',
      boxShadow:
        mode === 'light'
          ? 'inset 0 1.5px 0 rgba(255,255,255,0.6), inset 0 -1.5px 0 rgba(78,14,25,0.13), inset 0 0 18px rgba(255,255,255,0.14), 0 12px 30px rgba(255,107,107,0.22), 0 0 0 1px rgba(255,255,255,0.36)'
          : 'inset 0 1.5px 0 rgba(255,255,255,0.26), inset 0 -1.5px 0 rgba(255,107,107,0.1), inset 0 0 18px rgba(255,255,255,0.07), 0 14px 32px rgba(255,107,107,0.17), 0 0 22px rgba(255,107,107,0.08)',
      '&::before': {
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0) 62%)'
      },
      '&::after': {
        display: 'none'
      },
      '&:hover': {
        background:
          mode === 'light'
            ? 'linear-gradient(180deg, rgba(238,116,132,0.76), rgba(238,116,132,0.76))'
            : 'linear-gradient(180deg, rgba(207,91,112,0.72), rgba(207,91,112,0.72))',
        transform: 'translateY(-1px) scale(1.015)'
      }
    }
  }) satisfies Record<SiteButtonTone, object>;

export const SiteButton = ({
  tone = 'primary',
  mode = 'light',
  size = 'medium',
  sx,
  children,
  ...props
}: SiteButtonProps) => {
  const tokens = getDesignTokens(mode);
  const toneStyles = getToneStyles(mode, tokens);

  return (
    <Button
      disableElevation
      size={size}
      sx={{
        minHeight: size === 'small' ? 42 : size === 'large' ? 58 : 50,
        px: size === 'small' ? 2.6 : size === 'large' ? 4.5 : 3.6,
        borderRadius: 999,
        border: '1px solid',
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        backdropFilter: 'blur(14px) saturate(145%)',
        WebkitBackdropFilter: 'blur(14px) saturate(145%)',
        fontWeight: 900,
        fontSize: size === 'small' ? 14 : size === 'large' ? 21 : 16,
        lineHeight: 1.1,
        letterSpacing: 0,
        textTransform: 'none',
        transition:
          'transform 160ms ease, background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, opacity 160ms ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          zIndex: -1,
          borderRadius: 'inherit',
          background:
            mode === 'light'
              ? 'linear-gradient(180deg, rgba(255,255,255,0.42), rgba(255,255,255,0) 60%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0) 60%)',
          mixBlendMode: 'normal',
          pointerEvents: 'none'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '12%',
          bottom: '12%',
          left: '-42%',
          zIndex: -1,
          width: '34%',
          transform: 'rotate(16deg)',
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.24), transparent)',
          opacity: 0.34,
          transition: 'left 320ms ease, opacity 160ms ease',
          pointerEvents: 'none'
        },
        '&:hover::after': {
          left: '108%',
          opacity: 0.28
        },
        '&:focus-visible': {
          boxShadow: tokens.shadow.focus
        },
        '&.Mui-disabled': {
          color:
            mode === 'light' ? 'rgba(15,23,42,0.38)' : 'rgba(255,255,255,0.42)',
          background:
            mode === 'light'
              ? 'linear-gradient(135deg, rgba(255,255,255,0.48), rgba(15,23,42,0.035))'
              : 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.025))',
          borderColor: tokens.colors.border,
          boxShadow: 'none',
          backdropFilter: 'blur(10px) saturate(120%)',
          WebkitBackdropFilter: 'blur(10px) saturate(120%)',
          '&::after': {
            display: 'none'
          }
        },
        ...toneStyles[tone],
        ...sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
};
